import React, { useCallback, useContext, useMemo, useState } from 'react';
import { DocumentCarouselContext, IActionCriteria, IDocumentPrint } from '../../components/document/DocumentProvider';
import { useQuery } from '@apollo/client';
import {
  IPickingInstruction,
  IPickingInstructionDocumentCareouselQueryResult,
  IPickingInstructionDocumentCareouselQueryVariables,
  IPickingInstructionGenerateRangeMutationResult,
  IPickingInstructionGenerateRangeMutationVariables,
  IReprintPickingInstructionsMutationResult,
  IReprintPickingInstructionsMutationVariables,
  ISalesOrder,
  ISalesOrderDocumentCarouselQueryResult,
  ISalesOrderDocumentCarouselQueryVariables,
  ReportType,
} from '../../graphql/schema/p4types';
import PickingInstruction from '../../graphql/entities/sales/PickingInstruction';
import DocumentModalContext from '../../components/Context/DocumentModalContext';
import {
  getDocumentCarouselReportParameters,
  IDocumentValues,
  useActionFilters,
  useSalesOrderDeliveryRecipient,
  useDocumentCarouselDefaults,
} from '../../components/document/documentCarouselUtil';
import DocumentCarouselHeader from '../../components/document/DocumentCarouselHeader';
import { FormProvider, useForm } from 'react-hook-form';
import DocumentCarouselTemplate from '../../components/document/DocumentCarouselTemplate';
import DocumentCarouselOutput from '../../components/document/DocumentCarouselOutput';
import DocumentCarouselButtons from '../../components/document/DocumentCarouselButtons';
import { IJobMeta, useBackgroundMutation } from '../../utils/BackgroundJobs';
import { useDocumentCarouselComplete, useDocumentJobCompletion } from '../../utils/DocumentUtils';
import getClientMutationId from '../../utils/getClientMutationId';
import DocumentCarouselEmail from '../../components/document/DocumentCarouselEmail';
import SalesOrderDocumentCarouselQuery from '../../graphql/queries/document/SalesOrderDocumentCarouselQuery.graphql';
import PickingInstructionDocumentCarouselQuery from '../../graphql/queries/document/PickingInstructionDocumentCarouselQuery.graphql';
import DocumentCarouselPrintOptions from '../../components/document/DocumentCarouselPrintOptions';
import DocumentCarouselSummary from '../../components/document/DocumentCarouselSummary';
import { useCookies } from 'react-cookie';
import { DocumentCarouselHeaderInfo } from '../../components/document/DocumentCarouselHeaderInfo';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import DocumentCarouselSkeleton from '../../components/document/DocumentCarouselSkeleton';
import { DocumentCarouselPickingInstructionConfiguration } from '../../actions/sales/DocumentCarouselPickingInstructionConfiguration';

//Note - DocumentModal2 currently uses 'documentSummary' instead of 'DOCUMENT_SUMMARY' - Possible cause for minor friction in transition.
export const DOCUMENT_SUMMARY = 'DOCUMENT_SUMMARY';
export const PRINT_INDIVIDUAL_PREFERENCE_COOKIE = 'PRINT_INDIVIDUAL_PREFERENCE_COOKIE';

/**
 * Render the content for the Picking Instruction Document Carousel.
 * All querying should be completed before rendering this element so that 'documentDefaults' are accurate/up-to-date when the user see's them.
 * @param documentDefaults - Default document values. Should be final on initial load.
 * @param actionCriteria - The action criteria driving this particular print.
 * @param count - How many records are in this print job.
 * @param singleRecipient - Flag to indicate if all records are a single Customer/Supplier.
 * @param singlePrint - Flag to indicate if we're printing a single record.
 * @param singlePrintNode - If we're printing a single record, this should be the document-queried subset of that record for display/defaulting.
 *                        - Note - Will still be populated for non-single, will be arbitrarily the first record matching the criteria.
 * @param setCookie - Pass-through of cookie setter, used to update 'showSummary' and 'printContiguous' cookie values.
 * @param documentPrint - Print action object - Provides additional detail such as whether this is a reprint, print completion callback or additional email configuration.
 */
const PickingInstructionDocumentContent = ({
  documentDefaults,
  actionCriteria,
  count,
  singleRecipient,
  singlePrint,
  singlePrintNode,
  setCookie,
  documentPrint,
}: {
  documentDefaults: IDocumentValues;
  actionCriteria: IActionCriteria;
  count: number;
  singleRecipient: boolean;
  singlePrint: boolean;
  singlePrintNode: ISalesOrder | IPickingInstruction;
  setCookie: ReturnType<typeof useCookies>[1];
  documentPrint: IDocumentPrint;
}) => {
  const { onSkip } = useContext(DocumentCarouselContext);
  const { reprint, onPrintComplete } = documentPrint;
  const [reset, setReset] = useState<boolean>(false);

  //Use react-hook-form to manage our form values for better typing and performance than ConnectedForm.
  const methods = useForm<IDocumentValues>({
    defaultValues: documentDefaults,
  });
  const { getValues, watch } = methods;

  const onDocumentComplete = useDocumentCarouselComplete({
    watch,
    entity: PickingInstruction,
  });

  const onJobStart = useCallback(() => {
    onSkip(true);
  }, [onSkip]);

  const onJobComplete = useDocumentJobCompletion({
    evictionFieldNames: ['salesOrder'],
    onDocumentComplete,
    onPrintComplete,
  });

  const [printPickingInstructionRange] = useBackgroundMutation<
    IPickingInstructionGenerateRangeMutationResult,
    IPickingInstructionGenerateRangeMutationVariables
  >(PickingInstruction.generateRange!, {
    onStartJob: onJobStart,
    onCompleteJob: onJobComplete,
  });

  const [reprintPickingInstructionRange] = useBackgroundMutation<
    IReprintPickingInstructionsMutationResult,
    IReprintPickingInstructionsMutationVariables
  >(PickingInstruction.reprintRange!, {
    onStartJob: onJobStart,
    onCompleteJob: onJobComplete,
  });

  const onPrintClick = useCallback(() => {
    const reportParameters = getDocumentCarouselReportParameters({ getValues, reprint });

    if (reprint) {
      reprintPickingInstructionRange({
        variables: {
          input: {
            filters: actionCriteria.filters,
            pickingInstructions: normalizeVariables(actionCriteria.actionEntities),
            include: actionCriteria.include,
            clientMutationId: getClientMutationId(),
            reportParameters: reportParameters,
          },
        },
      });
    } else {
      printPickingInstructionRange({
        variables: {
          input: {
            filters: actionCriteria.filters,
            salesOrders: normalizeVariables(actionCriteria.actionEntities),
            include: actionCriteria.include,
            clientMutationId: getClientMutationId(),
            reportParameters: reportParameters,
            reset,
          },
        },
      });
    }
  }, [
    actionCriteria.actionEntities,
    actionCriteria.filters,
    actionCriteria.include,
    getValues,
    printPickingInstructionRange,
    reprint,
    reprintPickingInstructionRange,
  ]);

  const additionalRecipient = useSalesOrderDeliveryRecipient(singlePrintNode?.salesOrder ?? singlePrintNode);

  return (
    <FormProvider {...methods}>
      <DocumentCarouselHeader
        entity={PickingInstruction}
        count={count}
        secondaryTitle={
          <DocumentCarouselHeaderInfo
            contentLeft={
              singlePrint
                ? `Sales Order ${singlePrintNode?.salesOrder?.orderNumber || singlePrintNode?.orderNumber}`
                : undefined
            }
            contentRight={
              singleRecipient
                ? `Customer ${
                    (singlePrintNode?.salesOrder?.customer?.name || singlePrintNode?.customer?.name) ?? 'Unknown'
                  }`
                : undefined
            }
          />
        }
        reprint={reprint}
      />
      <div style={{ padding: '1em' }}>
        <DocumentCarouselTemplate reportType={ReportType.SalesPickingList} singleRecipient={singleRecipient} />
        <DocumentCarouselOutput singleRecipient={singleRecipient} />
        <DocumentCarouselSummary singlePrint={singlePrint} setCookie={setCookie} />
        <DocumentCarouselPrintOptions singlePrint={singlePrint} setCookie={setCookie} />
        <DocumentCarouselEmail
          entity={PickingInstruction}
          recipient={singlePrintNode?.customer}
          singleRecipient={singleRecipient}
          emailOptions={{
            additionalRecipients: additionalRecipient ? [additionalRecipient] : [],
            allowEmailTo: true,
            allowEmailCc: true,
            allowSubject: true,
          }}
        />
      </div>
      <DocumentCarouselPickingInstructionConfiguration reset={reset} onResetToggle={setReset} />
      <DocumentCarouselButtons onPrint={onPrintClick} />
    </FormProvider>
  );
};

/**
 * @param actionCriteria - The action criteria driving this particular print.
 * @param reprint - This is a reprint operation, will will use a different mutation and data queries.
 */
export const PickingInstructionDocumentCarouselCard = ({
  actionCriteria,
  documentPrint,
}: {
  actionCriteria: IActionCriteria;
  documentPrint: IDocumentPrint;
}) => {
  const { reprint } = documentPrint;

  const filters = useActionFilters(actionCriteria);
  const { data: salesOrderData, loading: salesOrderLoading } = useQuery<
    ISalesOrderDocumentCarouselQueryResult,
    ISalesOrderDocumentCarouselQueryVariables
  >(SalesOrderDocumentCarouselQuery, {
    variables: {
      filters: filters,
      first: 2,
      aggregateCriteria: {
        groupColumns: ['customer.id'],
        sumColumns: ['id'],
      },
      includeCustomer: true,
      includePickingInstructionDefault: true,
      skip: reprint,
    },
  });
  const { data: reprintData, loading: reprintLoading } = useQuery<
    IPickingInstructionDocumentCareouselQueryResult,
    IPickingInstructionDocumentCareouselQueryVariables
  >(PickingInstructionDocumentCarouselQuery, {
    variables: {
      filters: filters,
      first: 2,
      aggregateCriteria: {
        groupColumns: ['salesOrder.customer.id'],
        sumColumns: ['id'],
      },
      skip: !reprint,
    },
  });

  const data = useMemo(() => {
    return reprint ? reprintData : salesOrderData;
  }, [reprint, reprintData, salesOrderData]);
  const loading = salesOrderLoading || reprintLoading;

  const singlePrint: boolean = actionCriteria.include && actionCriteria.actionEntities.length === 1;
  //If the grouping gives a single Customer in the result, we can pull the document defaults off the Customer record and use it for the whole print.
  //(If there's >1 Customer, we cannot do this).
  const singleRecipient = data?.listData?.aggregateRows?.length === 1;
  const singlePrintNode = data?.listData?.edges?.[0]?.node as Maybe<ISalesOrder | IPickingInstruction>;

  const pickingInstructionDefault = data?.documentConfig?.pickingInstructionDefault;

  const [cookies, setCookie] = useCookies([DOCUMENT_SUMMARY, PRINT_INDIVIDUAL_PREFERENCE_COOKIE]);
  const documentDefaults = useDocumentCarouselDefaults({
    entity: PickingInstruction,
    commonDocumentDefault: pickingInstructionDefault,
    singleRecipient,
    recipient: singlePrintNode?.customer,
    cookies,
    allowEntityEmail: false,
  });

  if (data && !loading) {
    return (
      <PickingInstructionDocumentContent
        documentDefaults={documentDefaults}
        setCookie={setCookie}
        actionCriteria={actionCriteria}
        count={data?.listData?.count ?? 0}
        singleRecipient={singleRecipient}
        singlePrint={singlePrint}
        singlePrintNode={singlePrintNode}
        documentPrint={documentPrint}
      />
    );
  }

  return <DocumentCarouselSkeleton />;
};
